import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Attendee } from '@alucio/aws-beacon-amplify/src/models';
import { ATTENDEE_TYPE, MEETING_ATTENDEE_STATUS } from '@alucio/aws-beacon-amplify/src/API';
import { FormValuesType } from 'src/components/CustomFields/ComposableForm';
import { SPECIAL_FIELDS, VeevaVaultFormSetting } from 'src/classes/CRM/CRMVeevaVaultTypes';
import { ObjectWithId } from 'src/components/CustomFields/ComposableFormUtilities';
import { useGetFormConfigFromIndexDB } from 'src/state/redux/selector/crm';
import { CRM_SUBMIT_STATE } from 'src/components/Meeting/AddMeetingProvider';
import { useClearAttendeeSelectionError } from './useVeevaWatchFormHandler';
import { useVeevaVaultInquiryWatch } from './useVeevaVaultInquiryWatch';

// THIS HOOK WILL WATCH AND HANDLE ONCHANGE BEHAVIORS OF SPECIFIC VEEVA VAULT FIELDS WITHIN FORM
const useVeevaVaultWatchFormHandler = (rhForm: UseFormReturn<FormValuesType>,
  setCRMSubmitState: (error: CRM_SUBMIT_STATE) => void): void => {
  useAttendeeUsersWatch(rhForm);
  useVeevaVaultInquiryWatch(rhForm);
  useClearAttendeeSelectionError(rhForm, setCRMSubmitState);
};

// ** USERS' HANDLING ** //
// THIS SECTIONS AIMS TO HANDLE ADDING/REMOVING ATTENDEE RECORDS UPON USERS SELECTION IN ATTENDEES CONTROL FIELD //
const useAttendeeUsersWatch = (rhForm: UseFormReturn<FormValuesType>): void => {
  const formSettings = useGetFormConfigFromIndexDB();
  const arrWatchUsers = rhForm.watch(SPECIAL_FIELDS.USERS_ATTENDEES) as string[] | undefined;
  const [additionalAttendees] = rhForm.watch(['additionalAttendees']) as unknown as [Attendee[], Attendee[]];

  function onUsersPicklistChange(): void {
    if (Array.isArray(arrWatchUsers)) {
      const currentAttendees: Attendee[] = additionalAttendees || [];

      const updatedAttendees = currentAttendees.filter((attendee) =>
        attendee.attendeeType !== ATTENDEE_TYPE.CRM_USER ||
        (attendee.crmUserId && arrWatchUsers.includes(attendee.crmUserId)),
      );

      const newAttendees: Attendee[] = arrWatchUsers
        .filter((userId) => !currentAttendees.some((a) => a.crmUserId === userId))
        .map((userId) => ({
          id: userId,
          crmUserId: userId,
          name: getUserName(userId, formSettings.veevaVaultFormSettings),
          attendeeType: ATTENDEE_TYPE.CRM_USER,
          status: MEETING_ATTENDEE_STATUS.ACTIVE,
          updatedAt: new Date().toISOString(),
        } as Attendee));

      const result = [...updatedAttendees, ...newAttendees];
      rhForm.setValue('additionalAttendees', result as unknown as ObjectWithId[]);
    }
  }

  useEffect(onUsersPicklistChange, [arrWatchUsers]);
};

export function getUserName(userId: string, veevaVaultFormSettings: VeevaVaultFormSetting[]): string {
  const mainTableSetting = veevaVaultFormSettings.find(({ isMainTable }) => isMainTable);
  const usersLookup = mainTableSetting?.lookups[SPECIAL_FIELDS.USERS_ATTENDEES] ?? [];
  return usersLookup.find(({ id }) => id === userId)?.name__v ?? userId;
}

export default useVeevaVaultWatchFormHandler;
